/**
 * DocumentVault — coffre-fort enrichi : folders + tags + favoris + recherche.
 * Remplace la liste plate initiale par une navigation gauche + zone détail à droite.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Folder, FolderPlus, Star, Search, Tag, Upload, FileText, Trash2, Download, X, Pencil, Lock, Eye } from "lucide-react";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";

export const legalPreviewFile = (d) => d && ({
  src: `/legal/documents/${d.id}/preview`, downloadSrc: `/legal/documents/${d.id}/download`, name: d.filename,
});
import { toast } from "sonner";

const DOC_LABELS = {
  statuts: "Statuts", kbis: "KBIS", pv_assemblee: "PV Assemblée",
  rapport_gestion: "Rapport de gestion", convention: "Convention",
  attestation: "Attestation", contrat_associe: "Contrat associé",
  bilan: "Bilan", autre: "Autre",
};

const formatSize = (b) => b < 1024 ? `${b} o` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} Ko` : `${(b / 1024 / 1024).toFixed(1)} Mo`;
const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

export default function DocumentVault({ refreshKey }) {
  const [folders, setFolders] = useState([]);
  const [rootCount, setRootCount] = useState(0);
  const [docs, setDocs] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all"); // all | root | folder-id | favorites
  const [activeTag, setActiveTag] = useState(null);
  const [q, setQ] = useState("");
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [tagsDraft, setTagsDraft] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);

  const loadFolders = async () => {
    try {
      const r = await api.get("/legal/folders");
      setFolders(r.data.folders || []);
      setRootCount(r.data.root_count || 0);
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadDocs = async () => {
    try {
      const params = {};
      if (activeFolder === "favorites") params.favorite = true;
      else if (activeFolder === "root") params.folder_id = "root";
      else if (activeFolder !== "all") params.folder_id = activeFolder;
      if (activeTag) params.tag = activeTag;
      if (q) params.q = q;
      const r = await api.get("/legal/documents", { params });
      setDocs(r.data.documents || []);
      setAvailableTags(r.data.available_tags || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { loadDocs(); }, [activeFolder, activeTag, q, refreshKey]);

  const upload = async (e, category = "auto") => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const folderId = activeFolder !== "all" && activeFolder !== "favorites" && activeFolder !== "root" ? activeFolder : null;
    try {
      const params = { category };
      const r = await api.post("/legal/documents/upload", fd, { params });
      if (folderId) {
        await api.patch(`/legal/documents/${r.data.id}`, { folder_id: folderId });
      }
      toast.success("Document ajouté et chiffré — l'IA le classe et l'indexe en arrière-plan");
      loadDocs(); loadFolders();
      setTimeout(() => loadDocs(), 15000);
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post("/legal/folders", { name: newFolderName.trim() });
      toast.success("Dossier créé");
      setNewFolderName(""); setShowFolderDialog(false);
      loadFolders();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteFolder = async (fid) => {
    if (!window.confirm("Supprimer ce dossier ? Les documents seront détachés.")) return;
    try {
      await api.delete(`/legal/folders/${fid}`);
      toast.success("Dossier supprimé");
      if (activeFolder === fid) setActiveFolder("all");
      loadFolders(); loadDocs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFavorite = async (doc) => {
    try {
      await api.patch(`/legal/documents/${doc.id}`, { favorite: !doc.favorite });
      loadDocs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const moveTo = async (doc, folderId) => {
    try {
      await api.patch(`/legal/documents/${doc.id}`, { folder_id: folderId });
      toast.success("Déplacé");
      loadDocs(); loadFolders();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteDoc = async (did) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/legal/documents/${did}`); toast.success("Supprimé"); loadDocs(); loadFolders(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveTags = async () => {
    try {
      const tags = tagsDraft.split(",").map((t) => t.trim()).filter(Boolean);
      await api.patch(`/legal/documents/${editingTagsFor.id}`, { tags });
      toast.success("Tags mis à jour");
      setEditingTagsFor(null); loadDocs();
    } catch (e) { toast.error(apiError(e)); }
  };

  const favoriteCount = useMemo(() => docs.filter((d) => d.favorite).length, [docs]);

  return (
    <div className="space-y-3" data-testid="document-vault">
      {/* Search + upload bar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher par nom, tag…" value={q} onChange={(e) => setQ(e.target.value)}
                    className="pl-9" data-testid="vault-search" />
          </div>
          <label className="cursor-pointer">
            <input id="vault-upload-input" name="vault-upload-input"
              type="file" className="hidden" onChange={(e) => upload(e, "auto")} accept=".pdf,.png,.jpg,.jpeg,.docx" />
            <Button variant="outline" size="sm" asChild><span data-testid="vault-upload-btn"><Upload className="w-4 h-4 mr-1" />Uploader</span></Button>
          </label>
          <Button variant="outline" size="sm" onClick={() => setShowFolderDialog(true)} data-testid="vault-new-folder-btn">
            <FolderPlus className="w-4 h-4 mr-1" />Nouveau dossier
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
        {/* Sidebar */}
        <div className="space-y-1">
          <button onClick={() => setActiveFolder("all")}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center justify-between ${activeFolder === "all" ? "bg-blue-100 text-blue-700 font-medium" : "hover:bg-slate-100"}`}
                  data-testid="vault-folder-all">
            <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" />Tous les documents</span>
            <span className="text-xs text-slate-400">{docs.length}</span>
          </button>
          <button onClick={() => setActiveFolder("favorites")}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center justify-between ${activeFolder === "favorites" ? "bg-amber-100 text-amber-700 font-medium" : "hover:bg-slate-100"}`}
                  data-testid="vault-folder-favorites">
            <span className="flex items-center gap-2"><Star className="w-3.5 h-3.5" />Favoris</span>
            <span className="text-xs text-slate-400">{favoriteCount}</span>
          </button>
          <button onClick={() => setActiveFolder("root")}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center justify-between ${activeFolder === "root" ? "bg-blue-100 text-blue-700 font-medium" : "hover:bg-slate-100"}`}>
            <span className="flex items-center gap-2"><Folder className="w-3.5 h-3.5" />Racine</span>
            <span className="text-xs text-slate-400">{rootCount}</span>
          </button>
          <div className="pt-2 border-t">
            <p className="text-[10px] uppercase text-slate-500 px-3 mb-1">Dossiers</p>
            {folders.map((f) => (
              <div key={f.id} className={`group flex items-center rounded ${activeFolder === f.id ? "bg-blue-100 text-blue-700" : "hover:bg-slate-100"}`}>
                <button onClick={() => setActiveFolder(f.id)}
                        className="flex-1 text-left px-3 py-1.5 text-sm flex items-center justify-between"
                        data-testid={`vault-folder-${f.id}`}>
                  <span className="flex items-center gap-2 truncate"><Folder className="w-3.5 h-3.5" />{f.name}</span>
                  <span className="text-xs text-slate-400">{f.doc_count}</span>
                </button>
                <button onClick={() => deleteFolder(f.id)} className="hidden group-hover:block px-2 text-red-500 hover:text-red-700" title="Supprimer">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {folders.length === 0 && <p className="text-xs text-slate-400 px-3 py-1">Aucun dossier</p>}
          </div>

          {availableTags.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-[10px] uppercase text-slate-500 px-3 mb-1">Tags</p>
              <div className="flex flex-wrap gap-1 px-2">
                {availableTags.map((t) => (
                  <button key={t} onClick={() => setActiveTag(activeTag === t ? null : t)}
                          className={`text-[10px] px-2 py-0.5 rounded ${activeTag === t ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    #{t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Docs list */}
        <div className="space-y-2">
          {activeTag && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">Filtré par tag :</span>
              <Badge className="bg-purple-100 text-purple-700">#{activeTag}
                <button onClick={() => setActiveTag(null)} className="ml-1"><X className="w-3 h-3" /></button>
              </Badge>
            </div>
          )}
          {docs.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun document {q || activeTag ? "correspondant" : "dans cette vue"}.</p>
            </CardContent></Card>
          ) : (
            docs.map((d) => (
              <Card key={d.id} data-testid={`vault-doc-${d.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <button onClick={() => toggleFavorite(d)} title="Favori" data-testid={`vault-fav-${d.id}`}>
                    <Star className={`w-4 h-4 ${d.favorite ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                  </button>
                  <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setPreviewDoc(d)} title="Visualiser">
                    <p className="text-sm font-medium truncate flex items-center gap-1.5">
                      {d.encrypted && <Lock className="w-3 h-3 text-emerald-600 shrink-0" title="Chiffré" data-testid={`vault-lock-${d.id}`} />}
                      {d.filename}
                      {d.ai_status === "pending" && <span className="text-[10px] text-purple-600 font-normal">· IA en cours…</span>}
                      {d.ai_classified && <span className="text-[10px] text-purple-600 font-normal">· classé par l'IA</span>}
                    </p>
                    {d.ai_summary && <p className="text-xs text-slate-500 line-clamp-2" data-testid={`vault-summary-${d.id}`}>{d.ai_summary}</p>}
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="text-xs text-slate-500">{DOC_LABELS[d.category] || d.category} · {formatSize(d.size)} · {formatDateFR(d.uploaded_at)}</span>
                      {(d.tags || []).map((t) => (
                        <button key={t} onClick={() => setActiveTag(t)}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100">#{t}</button>
                      ))}
                    </div>
                  </div>
                  <select id={`vault-doc-folder-${d.id}`} name={`vault-doc-folder-${d.id}`}
                          value={d.folder_id || ""} onChange={(e) => moveTo(d, e.target.value || null)}
                          className="text-xs border rounded px-1 py-0.5 bg-white">
                    <option value="">— Racine —</option>
                    {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <Button variant="ghost" size="sm" onClick={() => { setEditingTagsFor(d); setTagsDraft((d.tags || []).join(", ")); }} title="Tags">
                    <Tag className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(d)} title="Visualiser" data-testid={`vault-view-${d.id}`}>
                    <Eye className="w-4 h-4 text-indigo-600" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Télécharger" data-testid={`vault-download-${d.id}`} onClick={() => window.open(api.defaults.baseURL + `/legal/documents/${d.id}/download`, "_blank")}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Supprimer" onClick={() => deleteDoc(d.id)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      <FilePreviewDialog file={legalPreviewFile(previewDoc)} onClose={() => setPreviewDoc(null)} />

      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau dossier</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div><Label>Nom</Label><Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                                          onKeyDown={(e) => e.key === "Enter" && createFolder()} data-testid="new-folder-name" /></div>
          <DialogFooter><Button onClick={createFolder} disabled={!newFolderName.trim()} data-testid="create-folder-btn">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingTagsFor} onOpenChange={() => setEditingTagsFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tags — {editingTagsFor?.filename}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div>
            <Label>Tags (séparés par des virgules)</Label>
            <Input value={tagsDraft} onChange={(e) => setTagsDraft(e.target.value)}
                    placeholder="urgent, 2024, à valider" data-testid="tags-input" />
          </div>
          <DialogFooter><Button onClick={saveTags} data-testid="save-tags-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
